import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

// TODO: remove once it's available in @ai/ui-components
export interface TableState {
  visibleColumns: string[]
  columnWidths: Record<string, number>
}

export function useConfiguratorTable(selectedConfiguratorColumns: Ref<string[] | undefined>) {
  const { t } = useI18n({ useScope: 'global' })

  const listColumns = computed(() => [
    { key: 'name', label: t('name') },
    { key: 'language', label: t('language') },
    { key: 'description', label: t('description') },
    { key: 'phone', label: t('phone') },
    { key: 'created_at', label: t('created_at') },
    { key: 'created_by', label: t('created_by') },
    { key: 'last_modification', label: t('updated_at') },
    { key: 'modified_by', label: t('modified_by') },
  ])

  const allColumns = computed(() => listColumns)
  const allColumnKeys = computed(() => listColumns.value.map((c) => c.key))

  const columnsOptions = computed(() =>
    listColumns.value.map((c) => ({ label: c.label, value: c.key, disabled: c.key === 'name' })),
  )

  // Table column widths, displayed columns and OTable tableState persistence
  const tableState = ref<TableState>()

  const normalizeKeys = (allKeys: string[], next?: string[] | null) => {
    const set = new Set(Array.isArray(next) ? next : [])
    set.add('name') // name is always visible and cannot be deselected
    const ordered = allKeys.filter((key) => set.has(key))
    return ordered.length ? ordered : ['name']
  }

  const tableStateModel = computed<TableState | undefined>({
    get: () => tableState.value,
    set: (next) => {
      if (!next) {
        tableState.value = undefined
        return
      }

      const normalized = normalizeKeys(allColumnKeys.value, next.visibleColumns)
      tableState.value = { ...next, visibleColumns: normalized }
      if (JSON.stringify(selectedConfiguratorColumns.value) !== JSON.stringify(normalized)) {
        selectedConfiguratorColumns.value = normalized
      }
    },
  })

  watch(selectedConfiguratorColumns, (selectedCols) => {
    const normalized = normalizeKeys(allColumnKeys.value, selectedCols)

    // update dropdown
    if (JSON.stringify(selectedConfiguratorColumns.value) !== JSON.stringify(normalized)) {
      selectedConfiguratorColumns.value = normalized
    }

    // update OTable tableState
    if (JSON.stringify(tableStateModel.value?.visibleColumns) !== JSON.stringify(normalized)) {
      tableStateModel.value = {
        visibleColumns: normalized,
        columnWidths: tableStateModel.value?.columnWidths ?? {},
      }
    }
  })

  return {
    listColumns,
    allColumns,
    allColumnKeys,
    columnsOptions,
    tableState,
    tableStateModel,
    normalizeKeys,
  }
}
